import { useState } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Image as ImageIcon } from 'lucide-react';
import { FloatingShapes } from './FloatingShapes';

const useTilt = () => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 150, damping: 15 });
  const sy = useSpring(my, { stiffness: 150, damping: 15 });
  const rotateY = useTransform(sx, [0, 1], [-12, 12]);
  const rotateX = useTransform(sy, [0, 1], [10, -10]);
  const glareX = useTransform(sx, [0, 1], ['0%', '100%']);
  const glareY = useTransform(sy, [0, 1], ['0%', '100%']);
  const glare = useTransform([glareX, glareY], ([x, y]) => `radial-gradient(circle at ${x} ${y}, rgba(255,255,255,0.55), transparent 55%)`);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); };
  return { rotateX, rotateY, glare, onMove, onLeave };
};

const ArrowBtn = ({ onClick, side, testId }) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    className={`absolute ${side === 'left' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white/95 shadow-soft flex items-center justify-center text-health-ink`}
    data-testid={testId}
  >
    {side === 'left' ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
  </motion.button>
);

export const TiltGallery = ({ images, videoUrl, name, discount }) => {
  const [[index, dir], setIndex] = useState([0, 0]);
  const [showVideo, setShowVideo] = useState(false);
  const tilt = useTilt();
  const go = (d) => setIndex(([i]) => [(i + d + images.length) % images.length, d]);
  const pick = (i) => { setIndex(([cur]) => [i, i > cur ? 1 : -1]); setShowVideo(false); };

  return (
    <div className="space-y-5" data-testid="product-image-container">
      <div className="relative" style={{ perspective: 1200 }}>
        <div className="absolute -inset-3 md:-inset-6 rounded-[2.5rem] bg-health-mint" />
        <FloatingShapes className="hidden md:block -inset-14 z-20" />
        <motion.div
          onMouseMove={tilt.onMove}
          onMouseLeave={tilt.onLeave}
          style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY, transformStyle: 'preserve-3d' }}
          className="relative aspect-square rounded-[2rem] overflow-hidden bg-white shadow-lift"
          data-testid="product-detail-image-gallery"
        >
          {showVideo && videoUrl ? (
            <video src={videoUrl} className="w-full h-full object-cover" controls autoPlay data-testid="product-video" />
          ) : (
            <AnimatePresence initial={false} custom={dir} mode="popLayout">
              <motion.img
                key={index}
                src={images[index]}
                alt={name}
                custom={dir}
                initial={{ opacity: 0, x: dir * 80, scale: 1.05 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: dir * -80, scale: 0.95 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="product-main-image"
              />
            </AnimatePresence>
          )}
          <motion.div
            className="pointer-events-none absolute inset-0 mix-blend-soft-light"
            style={{ background: tilt.glare }}
          />
          {discount > 0 && (
            <motion.span initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: -8 }} transition={{ type: 'spring', delay: 0.4 }} className="absolute top-5 left-5 z-10 bg-health-coral text-white font-heading font-bold text-lg px-4 py-2 rounded-2xl shadow-badge" data-testid="product-discount-badge">
              -{discount}%
            </motion.span>
          )}
          {!showVideo && images.length > 1 && (
            <>
              <ArrowBtn side="left" onClick={() => go(-1)} testId="gallery-prev" />
              <ArrowBtn side="right" onClick={() => go(1)} testId="gallery-next" />
            </>
          )}
          {videoUrl && (
            <button onClick={() => setShowVideo(!showVideo)} className="absolute bottom-4 right-4 z-20 bg-health-ink/85 text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2" data-testid="toggle-video-button">
              {showVideo ? <ImageIcon className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {showVideo ? 'View photos' : 'Watch video'}
            </button>
          )}
        </motion.div>
      </div>

      {(images.length > 1 || videoUrl) && (
        <div className="relative flex gap-3 overflow-x-auto pt-4 pb-2 px-1">
          {images.map((img, i) => (
            <motion.button key={i} whileHover={{ y: -4 }} whileTap={{ scale: 0.92 }} onClick={() => pick(i)} className="relative shrink-0 w-20 h-20 rounded-2xl overflow-hidden bg-white" data-testid={`gallery-thumb-${i}`}>
              <img src={img} alt={`${name} ${i + 1}`} className="w-full h-full object-cover" />
              {index === i && !showVideo && <motion.span layoutId="thumb-ring" className="absolute inset-0 rounded-2xl ring-4 ring-inset ring-health-emerald" />}
            </motion.button>
          ))}
          {videoUrl && (
            <motion.button whileHover={{ y: -4 }} onClick={() => setShowVideo(true)} className="relative shrink-0 w-20 h-20 rounded-2xl bg-health-ink flex items-center justify-center" data-testid="gallery-thumb-video">
              <Play className="h-7 w-7 text-white" />
              {showVideo && <motion.span layoutId="thumb-ring" className="absolute inset-0 rounded-2xl ring-4 ring-inset ring-health-emerald" />}
            </motion.button>
          )}
        </div>
      )}
    </div>
  );
};

export default TiltGallery;
